// src/lib/coverage/intakeStatus.ts
// ============================================================
// ONE STATUS VOCABULARY FOR THE THREE INTAKE CARDS
//
// Four values, one definition, shared. No card defines its own label or its own colour,
// because three cards each inventing a vocabulary is how "Complete" and "Uploaded" and a
// green tick came to mean three different things on one screen.
//
// ─────────────────────────────────────────────────────────────────────────────
// THE SECTIONS ARE DIFFERENT SHAPES, AND THE VOCABULARY RESPECTS THAT.
//
// Profile and Surveys are finite: there is a fixed set of things to answer, and answering
// them all is Complete. Document Upload is open-ended and has NO end state. A client who
// uploads a strategy deck outside the four named instruments has done something useful,
// and a fraction that reads 0 of 4 tells them they failed at something they were not
// attempting.
//
// So the STATUS BADGE is standardised and the supporting line is section-appropriate.
// Forcing one fraction across all three would produce a wrong answer on the third.
//
// COMPLETE CANNOT APPLY TO DOCUMENTS. That is enforced by the type, not by a convention:
// documentsStatus returns DocumentSectionStatus, which excludes it, so a future edit that
// tries to mark documents complete fails to compile.
// ─────────────────────────────────────────────────────────────────────────────
//
// GREY, NOT RED, FOR NOT STARTED. A client who has not uploaded anything has not done
// anything wrong. Red belongs to something actually failing, a rejected upload or an
// invalid field, and it stops meaning that the moment it is spent on an empty state.
//
// COLOUR IS NEVER THE ONLY SIGNAL. Every status carries a text label that states the whole
// meaning on its own, and a test asserts the label renders.
// ============================================================

export type IntakeStatus = 'not_started' | 'in_progress' | 'complete' | 'strengthened';

/** What the Document Upload section can be. `complete` is deliberately absent. */
export type DocumentSectionStatus = Exclude<IntakeStatus, 'complete'>;

export interface IntakeStatusMeta {
  /** Carries the FULL meaning on its own. Colour only supports it. */
  label: string;
  /** Tailwind text colour for the badge. */
  color: string;
  /** Tailwind background for the badge. */
  bgColor: string;
  /** Tailwind border, so the badge reads without relying on fill alone. */
  borderColor: string;
}

export const INTAKE_STATUS: Readonly<Record<IntakeStatus, IntakeStatusMeta>> = {
  not_started: {
    label: 'Not started',
    color: 'text-[#717182]',
    bgColor: 'bg-[#F5F7FA]',
    borderColor: 'border-[#e5e7eb]',
  },
  in_progress: {
    label: 'In progress',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  complete: {
    label: 'Complete',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
  },
  strengthened: {
    label: 'Strengthened',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
  },
};

export interface SectionState<S extends IntakeStatus = IntakeStatus> {
  status: S;
  /** The supporting line. Derived from the same numbers as the status. */
  detail: string;
}

// ---- copy helpers. Every one TAKES its numbers. ----

const plural = (n: number, word: string) => `${n} ${n === 1 ? word : `${word}s`}`;

/** Small numbers read better as words in a sentence. Beyond four, use the digit. */
const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four'];
const numberWord = (n: number) => NUMBER_WORDS[n] ?? String(n);

/**
 * Organization Profile. Finite and completable.
 *
 * The client-facing unit is a SECTION. Question counts are ours: "37 of 37 questions" is
 * an internal number that tells a client nothing they can act on.
 */
export function profileStatus(answered: number, total: number): SectionState {
  if (total > 0 && answered >= total) {
    return { status: 'complete', detail: 'All sections answered' };
  }
  if (answered <= 0) {
    return { status: 'not_started', detail: 'No sections answered yet' };
  }
  return { status: 'in_progress', detail: `${plural(total - answered, 'section')} remaining` };
}

/** Data Intake Surveys. Finite and completable. The unit is a MODULE, not a question. */
export function surveysStatus(answered: number, total: number): SectionState {
  if (total > 0 && answered >= total) {
    return { status: 'complete', detail: `All ${numberWord(total)} modules answered` };
  }
  if (answered <= 0) {
    return { status: 'not_started', detail: 'No modules answered yet' };
  }
  return { status: 'in_progress', detail: `${plural(total - answered, 'module')} remaining` };
}

/**
 * Document Upload. Open-ended, so it has no Complete.
 *
 *   no documents                          -> Not started
 *   documents, no named instrument matched -> In progress
 *   at least one named instrument matched  -> Strengthened
 *
 * THE INSTRUMENT COUNT IS INFORMATIONAL, NOT A TARGET. It exists to tell a client the
 * difference between three documents uploaded and three documents that moved measures. It
 * is deliberately not phrased or styled as progress toward four, because uploading
 * something outside the four named instruments is a legitimate thing to do and the card
 * must not read as though it failed.
 */
export function documentsStatus(
  documentCount: number,
  matchedInstruments: number,
  totalInstruments: number,
): SectionState<DocumentSectionStatus> {
  if (documentCount <= 0) {
    return { status: 'not_started', detail: 'No documents uploaded yet' };
  }
  const detail =
    `${plural(documentCount, 'document')} · ` +
    `${matchedInstruments} of ${totalInstruments} named instruments`;
  return { status: matchedInstruments > 0 ? 'strengthened' : 'in_progress', detail };
}
